'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Card, CardContent, CardHeader } from '@eliadmin/core/src/components/ui/card';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { PiDotOutlineFill } from 'react-icons/pi';
import { LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
type CategorySliderClassName = {
  main?: string;
  controlButton?: string;
  list?: string;
  listItem?: string;
  listItemImage?: string;
  listItemChevron?: string;
  link?: string;

  hoverCard?: string;
  hoverCardHeader?: string;
  hoverCardHeaderTitle?: string;
  hoverCardContent?: string;
  hoverCardList?: string;
  hoverCardListItem?: string;
  hoverCardLink?: string;
};

export interface CategorySliderProps {
  className?: CategorySliderClassName;
  style?: HTMLAttributes<HTMLElement>['style'];
  categories?: CategoriesEntity[];
  expandable?: boolean;
  showImage?: boolean;
}

export const CategorySlider = ({
  className,
  style,
  categories: propsCategories,
  expandable = true,
  showImage = true,
}: CategorySliderProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const me = useCustomerStore(s => s.me);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);
  const pathname = usePathname();

  const containerRef = useRef<HTMLUListElement | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [hoveredChild, setHoveredChild] = useState<number | null>(null);
  const [displayedChild, setDisplayedChild] = useState<number | null>(null);
  const [featuredProduct, setFeaturedProduct] = useState<Product | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);
  const [showButtons, setShowButtons] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const childHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const grandchildDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchRequestIdRef = useRef(0);
  const productCacheRef = useRef<Map<string | number, Product | null>>(new Map());

  const { t } = useTranslations();

  const scroll = (delta: number) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const currentScrollLeft = container.scrollLeft;
    container.scrollTo({
      left: currentScrollLeft + delta,
      behavior: 'smooth',
    });
  };

  const handleCategoryEnter = (index: number) => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (hoveredCategory !== index) {
      setFeaturedProduct(null);
      setDisplayedChild(null);
      setHoveredChild(null);
    }
    setHoveredCategory(index);
  };

  const handleGrandchildEnter = (grandchild: CategoriesEntity) => {
    if (!isCustomerInitialized) return;
    if (grandchildDebounceRef.current) clearTimeout(grandchildDebounceRef.current);

    if (productCacheRef.current.has(grandchild.id)) {
      setFeaturedProduct(productCacheRef.current.get(grandchild.id) ?? null);
      return;
    }

    const requestId = ++fetchRequestIdRef.current;
    setIsLoadingProduct(true);

    grandchildDebounceRef.current = setTimeout(async () => {
      try {
        const result = await fetchProducts({
          locale,
          query: buildProductsQuery({
            categories: [grandchild.id],
            results: 1,
            orderBy: 'views',
            order: 'desc',
          }),
          authToken: me?.authToken,
        });
        const product = result?.data?.[0] ?? null;
        productCacheRef.current.set(grandchild.id, product);
        if (fetchRequestIdRef.current === requestId) {
          setFeaturedProduct(product);
          setIsLoadingProduct(false);
        }
      } catch {
        if (fetchRequestIdRef.current === requestId) setIsLoadingProduct(false);
      }
    }, 150);
  };

  const handleGrandchildLeave = () => {
    if (grandchildDebounceRef.current) clearTimeout(grandchildDebounceRef.current);
  };

  const handleCategoryLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
      setHoveredChild(null);
    }, 100);
  };

  const handleChildEnter = (childIndex: number) => {
    if (childHideTimeoutRef.current) clearTimeout(childHideTimeoutRef.current);
    setHoveredChild(childIndex);
    setDisplayedChild(childIndex);
  };

  const handleChildLeave = () => {
    childHideTimeoutRef.current = setTimeout(() => setHoveredChild(null), 100);
  };

  useEffect(() => {
    productCacheRef.current.clear();
    setFeaturedProduct(null);
  }, [me?.authToken]);

  useEffect(() => {
    setNavigatingTo(null);
    setHoveredCategory(null);
    setHoveredChild(null);
    setDisplayedChild(null);
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        if (container.scrollWidth > container.clientWidth) {
          setShowButtons(true);
        } else {
          setShowButtons(false);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categories = propsCategories;

  return (
    <nav
      className={cn(
        'relative flex h-full items-center justify-center bg-primary py-1',
        'ea-category-slider__main',
        className?.main,
      )}
      style={style}
    >
      {showButtons && (
        <div className="h-[36px]">
          <LuChevronLeft
            onClick={() => scroll(-100)}
            className={cn(
              'mr-auto h-full min-h-full w-[22px] min-w-[22px] cursor-pointer rounded-s-[4px] border-none bg-primary text-white ',
              'ea-category-slider__control-button',
              className?.controlButton,
            )}
          />
        </div>
      )}
      <ul
        ref={containerRef}
        className={cn(
          'hide-scroll-bar flex flex-row items-center justify-between overflow-x-scroll will-change-scroll',
          'ea-category-slider__list',
          className?.list,
        )}
      >
        {categories?.map((category, index) => (
          <li
            key={category.id ?? category.slug}
            className={cn(
              'flex min-w-max flex-row items-center border-none px-1 py-1',
              'ea-category-slider__list-item',
              className?.listItem,
            )}
            onMouseEnter={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryEnter(index);
            }}
            onMouseLeave={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryLeave();
            }}
          >
            <Link prefetchOnHover
              href={category.slug}
              className={cn(
                'flex flex-row items-center gap-2 text-[14px] font-semibold hover:underline',
                'ea-category-slider__list-item-link',
                className?.link,
              )}
              prefetch={false}
            >
              {category.image && showImage && (
                <Image
                  alt={resolveImageAlt(category?.image, category?.name, undefined)}
                  src={category.image.url}
                  width={0}
                  height={0}
                  sizes={'100vw'}
                  className={cn(
                    'h-5 w-5',
                    'ea-category-slider__list-item-image',
                    className?.listItemImage,
                  )}
                />
              )}
              {category.name}
            </Link>
            {!!category.childs?.length && expandable && (
              <LuChevronDown
                className={cn(
                  'mx-2 inline-block stroke-primary',
                  'ea-category-slider__list-item-chevron',
                  className?.listItemChevron,
                )}
                size={16}
              />
            )}
            <div
              className={cn(
                'absolute left-1/2 top-full z-20 mt-2 flex w-full -translate-x-1/2 items-stretch justify-between',
                'hide-scroll-bar pointer-events-none invisible max-h-[70dvh] overflow-y-scroll opacity-0 transition-all duration-200 ease-in-out',
                hoveredCategory === index && 'pointer-events-auto visible opacity-100',
                'ea-hovered-category__content',
              )}
              onMouseEnter={() => {
                if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
              }}
              onMouseLeave={handleCategoryLeave}
            >
              <div className="flex w-full max-w-[378px] flex-col items-start justify-start gap-4 bg-[#F4F5FB] p-6">
                {category.childs?.map((child, childIndex) => (
                  <Link prefetchOnHover
                    key={child.id ?? child.slug}
                    href={child.slug}
                    prefetch={false}
                    className={cn(
                      'flex w-full items-center justify-start gap-2 text-sm font-bold text-black transition-all lg:text-base',
                      hoveredChild === childIndex &&
                        'ea-hovered-category__child-item--active hover:opacity-75',
                      'ea-hovered-category__child-item',
                    )}
                    onMouseEnter={() => handleChildEnter(childIndex)}
                    onMouseLeave={handleChildLeave}
                    onClick={() => setNavigatingTo(child.slug)}
                  >
                    {child?.icon?.url && (
                      <Image
                        src={child?.icon?.url ?? '/images/placeholder.png'}
                        alt={resolveImageAlt(child?.icon, child?.name)}
                        width={32}
                        height={32}
                        className="shrink-0 object-contain"
                      />
                    )}

                    {child.name}
                    {navigatingTo === child.slug && <Spinner className="ml-auto shrink-0" />}
                  </Link>
                ))}
              </div>
              <div
                className="flex w-full items-start justify-between gap-6 bg-white p-6"
                onMouseEnter={() => {
                  if (childHideTimeoutRef.current) clearTimeout(childHideTimeoutRef.current);
                }}
                onMouseLeave={handleChildLeave}
              >
                <div
                  className={cn(
                    'grid w-full grid-cols-1 gap-4 transition-opacity duration-200 ease-in-out lg:grid-cols-2 lg:gap-6',
                    hoveredChild !== null ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  {displayedChild !== null &&
                    category.childs?.[displayedChild]?.childs?.map(
                      (grandchild, grandchildIndex) => (
                        <Link prefetchOnHover
                          key={grandchild.id ?? grandchild.slug ?? grandchildIndex}
                          href={grandchild.slug}
                          prefetch={false}
                          className="ea-hovered-category__grandchild-item flex items-center justify-start gap-2 font-medium text-black"
                          onMouseEnter={() => handleGrandchildEnter(grandchild)}
                          onMouseLeave={handleGrandchildLeave}
                          onClick={() => setNavigatingTo(grandchild.slug)}
                        >
                          {navigatingTo === grandchild.slug ? (
                            <Spinner size="sm" className="text-primary" />
                          ) : (
                            <MdKeyboardArrowRight size={16} className="text-primary" />
                          )}
                          {grandchild.name}
                        </Link>
                      ),
                    )}
                </div>
                <div
                  className={cn(
                    'flex w-full max-w-[218px] flex-col items-start justify-start gap-3 border border-border p-3',
                    'transition-opacity duration-200 ease-in-out',
                    !featuredProduct && !isLoadingProduct
                      ? 'pointer-events-none opacity-0'
                      : 'opacity-100',
                  )}
                >
                  {isLoadingProduct ? (
                    <>
                      <Skeleton className="aspect-square w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </>
                  ) : featuredProduct ? (
                    <Link prefetchOnHover
                      href={featuredProduct.slug}
                      prefetch={false}
                      className="flex w-full flex-col gap-3"
                      onClick={() => setNavigatingTo(featuredProduct.slug)}
                    >
                      <div className="relative aspect-square w-full">
                        <Image
                          src={featuredProduct.image?.url ?? '/images/placeholder.png'}
                          alt={resolveImageAlt(featuredProduct?.image, featuredProduct?.name)}
                          fill
                          className="absolute inset-0 z-0 h-full w-full object-contain p-3"
                        />
                      </div>
                      <h4 className="line-clamp-2 flex items-center gap-2 text-sm font-bold text-foreground">
                        {featuredProduct.name}
                        {navigatingTo === featuredProduct.slug && (
                          <Spinner size="sm" className="shrink-0" />
                        )}
                      </h4>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {showButtons && (
        <div className="h-[36px]">
          <LuChevronRight
            onClick={() => scroll(100)}
            className={cn(
              'ml-auto h-full min-h-full w-[22px] min-w-[22px] cursor-pointer rounded-e-[4px] border-none text-white ',
              'ea-category-slider__control-button ea-category-slider__control-button-right',
              className?.controlButton,
            )}
          />
        </div>
      )}
    </nav>
  );
};
